/**
 * StrategyPerformanceDrawer
 * Slide-over panel showing historical trade P&L for a single strategy.
 * RU-STRATDRILL: the one shared drill-down (SD-1..SD-9), opened from any screen through openStrategyDrawer():
 * metrics, risk, breakdowns, paper vs backtest, "why this strategy", qualification, repair history, trades.
 * Read-only.
 */
import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import { useEffect, useState } from "react";
import dslToPlainEnglish from "@/lib/dslToPlainEnglish";
import api from "@/lib/api";
import { X, TrendingUp, TrendingDown, BarChart2, Loader2 } from "lucide-react";
import SimulatedBadge from "@/components/paper/SimulatedBadge";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";

const PERIODS = [
  { key: "1w",  label: "1W" },
  { key: "1m",  label: "1M" },
  { key: "3m",  label: "3M" },
  { key: "1y",  label: "1Y" },
  { key: "all", label: "All" },
];

const fmt = (n) => {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(Number(n));
  const sign = Number(n) >= 0 ? "+" : "−";
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
  return `${sign}₹${abs.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
};

function PnlTone({ value, children }) {
  const cls = value > 0 ? "txt-profit" : value < 0 ? "txt-loss" : "txt-muted";
  return <span className={cls}>{children}</span>;
}

function MetricCard({ label, value, tone }) {
  const cls = tone === "profit" ? "txt-profit" : tone === "loss" ? "txt-loss" : "txt-secondary";
  return (
    <div className="border border-[var(--border)] p-3 space-y-1">
      <div className="overline txt-muted">{label}</div>
      <div className={`font-mono-data text-base font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value ?? 0;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] px-3 py-2 text-xs">
      <div className="txt-muted mb-1">{label}</div>
      <div className={val >= 0 ? "txt-profit" : "txt-loss"}>
        {val >= 0 ? "+" : ""}₹{Math.abs(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
};

const num = (v, d = 2) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
const pctTxt = (v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`);
const fmtLoss = (v) => (v ? fmt(-v) : "₹0.00");   // SD-17: a zero drawdown has no sign
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
                                     timeZone: "Asia/Kolkata" });
};
const EXIT_LABEL = { stop: "Stop", target: "Target", rule: "Exit rule", time: "Time", manual: "Manual", other: "Other" };

export function contractText(t) {
  if (String(t.instrument_type || "").toUpperCase() === "OPT" || t.option_strike) {
    return [t.symbol, t.option_strike, t.option_kind, t.option_expiry].filter((x) => x != null && x !== "").join(" ");
  }
  return [t.symbol, t.instrument_type].filter(Boolean).join(" ");
}

function Section({ title, testid, children }) {
  return (
    <section className="px-6 pb-5" data-testid={testid}>
      <div className="overline txt-muted mb-2">{title}</div>
      {children}
    </section>
  );
}

function KV({ rows }) {
  return (
    <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <div className="txt-muted">{k}</div>
          <div className="text-[var(--text-primary)] break-words">{v}</div>
        </div>
      ))}
    </div>
  );
}

function BucketTable({ title, rows, label = (k) => k, testid }) {
  return (
    <div className="border border-[var(--border)]" data-testid={testid}>
      <div className="px-2 py-1 text-[10px] font-section txt-muted bg-[var(--bg-surface-2)]">{title}</div>
      {rows.length === 0 ? <div className="px-2 py-1 text-xs txt-muted">—</div> : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map((b) => (
              <tr key={String(b.key)} className="border-t border-[var(--border)]">
                <td className="px-2 py-1">{label(b.key)}</td>
                <td className="px-2 py-1 text-right font-mono-data txt-secondary">{b.trades}</td>
                <td className="px-2 py-1 text-right font-mono-data txt-secondary">{pctTxt(b.win_rate)}</td>
                <td className="px-2 py-1 text-right font-mono-data"><PnlTone value={b.pnl}>{fmt(b.pnl)}</PnlTone></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MetricsBlock({ m }) {
  if (!m) return <span className="txt-muted">—</span>;
  return (
    <div className="font-mono-data text-xs space-y-0.5">
      {m.trades != null && <div>{m.trades} trades</div>}
      {m.win_rate_pct != null && <div>win {pctTxt(m.win_rate_pct)}</div>}
      {m.profit_factor != null && <div>PF {num(m.profit_factor)}</div>}
      {m.net_pnl != null && <div>net {fmt(m.net_pnl)}</div>}
    </div>
  );
}

function Repairs({ repairs, onOpen }) {
  const list = repairs?.repairs || [];
  const versions = repairs?.versions || [];
  return (
    <div className="space-y-3">
      {list.length === 0 ? <div className="text-xs txt-muted" data-testid="drawer-no-repairs">No repairs</div> : list.map((r) => (
        <div key={r.id} className="border border-[var(--border)] p-3 space-y-2 text-xs" data-testid={`drawer-repair-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-section">v{r.parent_version} → v{r.version}</span>
            <span className="txt-muted">{fmtDate(r.when)}</span>
            <span className="txt-secondary">{r.outcome}</span>
            {r.active && <span className="text-[var(--profit)] font-section" data-testid={`drawer-repair-active-${r.id}`}>ACTIVE</span>}
          </div>
          <div className="txt-secondary">Trigger: {r.trigger || "trigger not recorded"}</div>
          {r.diffs?.length ? (
            <ul className="font-mono-data space-y-0.5" data-testid={`drawer-repair-diffs-${r.id}`}>
              {r.diffs.map((d) => (
                <li key={d.path}>{d.path}: {JSON.stringify(d.old)} → {JSON.stringify(d.new)}</li>
              ))}
            </ul>
          ) : <div className="txt-muted">No parameter changes recorded</div>}
          <div className="grid grid-cols-2 gap-2">
            <div><div className="txt-muted mb-0.5">Before (v{r.parent_version})</div>
              <div className="txt-muted">backtest</div><MetricsBlock m={r.before?.backtest} />
              <div className="txt-muted mt-1">paper</div><MetricsBlock m={r.before?.paper} /></div>
            <div><div className="txt-muted mb-0.5">After (v{r.version})</div>
              <div className="txt-muted">backtest</div><MetricsBlock m={r.after?.backtest} />
              <div className="txt-muted mt-1">paper</div><MetricsBlock m={r.after?.paper} /></div>
          </div>
        </div>
      ))}
      {versions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs" data-testid="drawer-lineage">
          {versions.map((v) => (
            <button key={v.id} type="button" onClick={() => onOpen({ id: v.id, name: v.name })}
                    data-testid={`drawer-version-${v.id}`}
                    className={`px-2 py-0.5 border ${v.active ? "border-[var(--profit)]" : "border-[var(--border)]"} txt-secondary hover:text-[var(--text-primary)]`}>
              v{v.version}{v.active ? " · active" : v.status ? ` · ${v.status}` : ""}
            </button>
          ))}
        </div>
      )}
      <div className="text-[10px] txt-muted">
        Not recorded: repair searches that found no better candidate, and a version's status before it was retired.
      </div>
    </div>
  );
}

export default function StrategyPerformanceDrawer({ strategy, onClose }) {
  const [current, setCurrent] = useState(strategy);
  const [period, setPeriod]   = useState("all");
  const [data, setData]       = useState(null);
  const [ins, setIns]         = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setCurrent(strategy); setPeriod("all"); }, [strategy]);
  // SD-17: another version opens on the full history
  const switchTo = (v) => { setCurrent(v); setPeriod("all"); };

  const sid = current?.id;
  useEffect(() => {
    if (!sid) return;
    let live = true;
    setLoading(true);
    Promise.all([
      api.get(`/strategies/${sid}/performance`, { params: { period } }).then((r) => r.data).catch(() => null),
      api.get(`/strategies/${sid}/insights`, { params: { period } }).then((r) => r.data).catch(() => null),
    ]).then(([perf, insights]) => {
      if (!live) return;
      setData(perf);
      setIns(insights && insights.strategy ? insights : null);
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [sid, period]);

  if (!current) return null;

  const doc = ins?.strategy || current;
  const name = current.name || doc.name || "Strategy";
  const dsl = doc.dsl || current.dsl || {};
  const s = data?.summary;
  const trades = data?.trades ?? [];
  const chart  = data?.chart  ?? [];
  const risk = ins?.risk;
  const bd = ins?.breakdowns;
  const pvb = ins?.paper_vs_backtest;
  const why = ins?.why;
  const qual = ins?.qualification;
  const plain = dslToPlainEnglish(dsl, ins?.exit_defaults);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="strategy-drawer">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" onClick={onClose} />

      {/* Drawer */}
      <div className="w-full max-w-2xl bg-[var(--bg-surface)] border-l border-[var(--border)] flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <div className="font-display text-xl flex items-center gap-2 flex-wrap" data-testid="strategy-drawer-name">
              {name}
              <StrategyRefChip id={current.id} testid="strategy-perf-ref" openable={false} />
              <StrategyStatusChip src={doc} testid="strategy-perf-status" />
            </div>
            <div className="text-xs txt-muted mt-0.5">
              Trade performance — {dsl.symbol || "—"} · {dsl.timeframe || "1d"}
            </div>
          </div>
          <button onClick={onClose} className="txt-muted hover:text-[var(--text-primary)]" aria-label="Close" data-testid="strategy-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Period selector */}
        <div className="flex gap-1 px-6 py-3 border-b border-[var(--border)] shrink-0">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`strategy-drawer-period-${p.key}`}
              className={`px-3 py-1 text-xs font-section transition-colors ${
                period === p.key
                  ? "bg-[var(--text-primary)] text-[var(--bg-page)]"
                  : "txt-muted hover:text-[var(--text-primary)] border border-[var(--border)]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin txt-muted" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pt-5">
            {/* (a) metrics */}
            <Section title="Performance · per closed fill" testid="drawer-metrics">
              {!data ? (
                <div className="flex flex-col items-center gap-2 txt-muted py-4">
                  <BarChart2 className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No performance data available</p>
                  <p className="text-xs">Trade this strategy in paper or live mode to build history.</p>
                </div>
              ) : trades.length === 0 ? (
                <div className="flex flex-col items-center gap-2 txt-muted py-4">
                  <BarChart2 className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No closed trades in this period</p>
                  <p className="text-xs">Trades appear here after positions are closed.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <MetricCard label="Total P&L" value={fmt(s?.total_pnl)}
                      tone={s?.total_pnl > 0 ? "profit" : s?.total_pnl < 0 ? "loss" : "neutral"} />
                    <MetricCard label="Win Rate" value={s?.win_rate != null ? `${s.win_rate}%` : "—"} />
                    <MetricCard label="Trades" value={s?.trade_count ?? "—"} />
                  </div>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <MetricCard label="Avg P&L" value={fmt(s?.avg_pnl)} />
                    <MetricCard label="Best Trade" value={fmt(s?.max_win)} tone="profit" />
                    <MetricCard label="Worst Trade" value={fmt(s?.max_loss)} tone="loss" />
                    <MetricCard label="Profit Factor" value={s?.profit_factor?.toFixed(2) ?? "—"} />
                  </div>
                  <div className="overline txt-muted mb-2">Cumulative P&L</div>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chart} margin={{ left: 0, right: 4, top: 4, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.4} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--text-muted)" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10, fill: "var(--text-muted)" }} tickLine={false} axisLine={false} width={56}
                          tickFormatter={v => v >= 0 ? `+${(v/1000).toFixed(0)}k` : `${(v/1000).toFixed(0)}k`} />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="cumulative_pnl"
                          stroke={s?.total_pnl >= 0 ? "var(--profit)" : "var(--loss)"}
                          strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </Section>

            {!ins ? (
              <Section title="Details" testid="drawer-details-unavailable">
                <div className="text-xs txt-muted">Strategy details unavailable (not your strategy, or not loaded).</div>
              </Section>
            ) : (
              <>
                {/* (b) risk */}
                <Section title="Risk · per round trip" testid="drawer-risk">
                  <div className="grid grid-cols-4 gap-2">
                    <MetricCard label="Max drawdown" value={fmtLoss(risk?.max_drawdown)} tone={risk?.max_drawdown ? "loss" : "neutral"} />
                    <MetricCard label="Expectancy / trade" value={fmt(risk?.expectancy)} />
                    <MetricCard label="Longest win streak" value={risk?.longest_win_streak ?? "—"} />
                    <MetricCard label="Longest loss streak" value={risk?.longest_loss_streak ?? "—"} />
                  </div>
                </Section>

                {/* (c) breakdowns */}
                <Section title="Breakdowns · per round trip (trades · win % · P&L)" testid="drawer-breakdowns">
                  <div className="grid grid-cols-2 gap-2">
                    <BucketTable title="By symbol" rows={bd?.by_symbol || []} testid="drawer-by-symbol" />
                    <BucketTable title="By hour (IST)" rows={bd?.by_hour || []} testid="drawer-by-hour"
                                 label={(k) => (k == null ? "—" : `${String(k).padStart(2, "0")}:00`)} />
                    <BucketTable title="By exit reason" rows={bd?.by_exit || []} testid="drawer-by-exit"
                                 label={(k) => EXIT_LABEL[k] || k} />
                    <BucketTable title="Long vs short" rows={bd?.by_direction || []} testid="drawer-by-direction"
                                 label={(k) => (k === "short" ? "Short" : "Long")} />
                  </div>
                </Section>

                {/* (d) paper vs backtest */}
                <Section title="Paper vs backtest" testid="drawer-paper-vs-backtest">
                  <table className="w-full text-xs border border-[var(--border)]">
                    <thead>
                      <tr className="bg-[var(--bg-surface-2)]">
                        <th className="text-left px-2 py-1 txt-muted font-normal">Metric</th>
                        <th className="text-right px-2 py-1 txt-muted font-normal">Paper</th>
                        <th className="text-right px-2 py-1 txt-muted font-normal" data-testid="drawer-backtest-source">
                          Backtest{pvb?.backtest?.source ? ` (${pvb.backtest.source})` : ""}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="font-mono-data">
                      {[
                        ["Trades", pvb?.paper?.trades ?? "—", pvb?.backtest?.trades ?? "—"],
                        ["Win rate", pctTxt(pvb?.paper?.win_rate_pct), pctTxt(pvb?.backtest?.win_rate_pct)],
                        ["Profit factor", num(pvb?.paper?.profit_factor), num(pvb?.backtest?.profit_factor)],
                        ["Max drawdown", pvb?.paper?.max_drawdown != null ? fmtLoss(pvb.paper.max_drawdown) : "—",
                          pvb?.backtest?.max_drawdown_pct != null ? `${num(pvb.backtest.max_drawdown_pct, 1)}%` : "—"],
                        ["Net / return", fmt(pvb?.paper?.net_pnl),
                          pvb?.backtest?.total_return_pct != null ? `${num(pvb.backtest.total_return_pct, 2)}%` : "—"],
                      ].map(([k, a, b]) => (
                        <tr key={k} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1 font-sans txt-secondary">{k}</td>
                          <td className="px-2 py-1 text-right">{a}</td>
                          <td className="px-2 py-1 text-right">{b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!pvb?.backtest?.source && <div className="text-xs txt-muted mt-1">No backtest stored.</div>}
                </Section>

                {/* (e) why this strategy */}
                <Section title="Why this strategy" testid="drawer-why">
                  {why?.thesis ? (
                    <div className="text-xs mb-2" data-testid="drawer-thesis">
                      <div className="txt-secondary mb-0.5">
                        {why.thesis.label} · {why.thesis.kind} · {why.thesis.basis} · {why.thesis.suite_label}
                        {why.thesis.variant ? ` · ${why.thesis.variant}` : ""}
                      </div>
                      <div className="text-[var(--text-primary)]">{why.thesis.thesis}</div>
                    </div>
                  ) : why?.description ? (
                    <div className="text-xs text-[var(--text-primary)] mb-2" data-testid="drawer-description">{why.description}</div>
                  ) : null}
                  <div data-testid="drawer-plain-dsl"><KV rows={plain.map((l) => [l.label, l.text])} /></div>
                  <div className="mt-2" data-testid="drawer-origin">
                    <KV rows={[
                      ["Origin", `${why?.origin?.kind || "—"} — ${why?.origin?.detail || ""}`],
                      ["Created", fmtTime(why?.origin?.created_at)],
                      ["Updated", fmtTime(why?.origin?.updated_at)],
                      ["Version", doc.version ? `v${doc.version}` : "—"],
                      ["Edit history", why?.edit_versions?.length
                        ? why.edit_versions.map((v) => `v${v.version} ${fmtDate(v.saved_at)}`).join(", ")
                        : "no edits saved"],
                    ]} />
                  </div>
                </Section>

                {/* (f) qualification */}
                <Section title="Qualification" testid="drawer-qualification">
                  {!qual?.in_suite ? (
                    <div className="text-xs txt-muted">Not in the qualification suite.</div>
                  ) : (
                    <>
                      <KV rows={[
                        ["Status", (qual.status || "incubating").toUpperCase()],
                        ["Basis", qual.basis || "—"],
                        ["Checked", fmtTime(qual.checked_at)],
                        ["Live gate", qual.gate_passed ? "passed" : `not passed${qual.gate_note ? ` — ${qual.gate_note}` : ""}`],
                      ]} />
                      <ul className="mt-2 text-xs list-disc pl-4 space-y-0.5 txt-secondary" data-testid="drawer-needs">
                        {(qual.needs || []).map((n) => <li key={n}>{n}</li>)}
                      </ul>
                    </>
                  )}
                </Section>

                {/* (g) repair history */}
                <Section title="Repair history" testid="drawer-repairs">
                  <Repairs repairs={ins.repairs} onOpen={switchTo} />
                </Section>
              </>
            )}

            {/* (h) trade list */}
            {trades.length > 0 && (
              <Section title={`Trades (${trades.length})`} testid="drawer-trades">
                <div className="border border-[var(--border)] overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[var(--border)] bg-[var(--bg-page)]">
                        <th className="text-left overline px-2 py-2">Contract</th>
                        <th className="text-left overline px-2 py-2">Dir</th>
                        <th className="text-left overline px-2 py-2">Entry</th>
                        <th className="text-left overline px-2 py-2">Exit</th>
                        <th className="text-right overline px-2 py-2">P&L</th>
                        <th className="text-right overline px-2 py-2">%</th>
                        <th className="text-left overline px-2 py-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trades.map((t) => (
                        <tr key={t.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-page)]" data-testid={`drawer-trade-${t.id}`}>
                          <td className="px-2 py-2 font-section">
                            {contractText(t)}
                            {t.simulated ? <span className="ml-1.5"><SimulatedBadge /></span> : null}
                          </td>
                          <td className="px-2 py-2">
                            {t.direction === "LONG"
                              ? <TrendingUp className="w-3.5 h-3.5 txt-profit" />
                              : <TrendingDown className="w-3.5 h-3.5 txt-loss" />}
                          </td>
                          <td className="px-2 py-2 font-mono-data whitespace-nowrap">{fmtTime(t.entry_at)} · ₹{Number(t.entry_price).toLocaleString("en-IN")}</td>
                          <td className="px-2 py-2 font-mono-data whitespace-nowrap">{fmtTime(t.exit_at)} · {t.exit_price == null ? "open" : `₹${Number(t.exit_price).toLocaleString("en-IN")}`}</td>
                          <td className="px-2 py-2 font-mono-data text-right"><PnlTone value={t.pnl}>{fmt(t.pnl)}</PnlTone></td>
                          <td className="px-2 py-2 font-mono-data text-right">
                            <PnlTone value={t.pnl_pct}>
                              {t.pnl_pct == null ? "—" : `${t.pnl_pct > 0 ? "+" : ""}${Number(t.pnl_pct).toFixed(2)}%`}
                            </PnlTone>
                          </td>
                          <td className="px-2 py-2 txt-secondary">{t.exit_reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
